import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Phone, MapPin, Mail, Loader2, HeartHandshake } from "lucide-react";
import { toast } from "sonner";
import { CHURCH, CONTACT_SUBJECTS, IMAGES } from "../lib/data";
import { PageHeader } from "../components/PageHeader";
import { Eyebrow, Field, Btn } from "../components/ui-kit";
import { Reveal } from "../lib/motion";
import { api } from "../lib/api";

export default function Contact() {
  const [params] = useSearchParams();
  const [c, setC] = useState({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "" });
  const [p, setP] = useState({ name: "", email: "", phone: "", request: "", keep_private: false });
  const [cl, setCl] = useState(false);
  const [pl, setPl] = useState(false);

  useEffect(() => {
    const s = params.get("subject");
    if (s) setC((prev) => ({ ...prev, subject: s }));
    if (window.location.hash === "#prayer") {
      setTimeout(() => document.getElementById("prayer")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, [params]);

  const ch = (setter) => (e) => setter((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const submitContact = async (e) => {
    e.preventDefault(); setCl(true);
    try {
      const { data } = await api.post("/forms/contact", c);
      toast.success(data.message);
      setC({ first_name: "", last_name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast.error("Something went wrong. Please try again."); }
    setCl(false);
  };
  const submitPrayer = async (e) => {
    e.preventDefault(); setPl(true);
    try {
      const { data } = await api.post("/forms/prayer", p);
      toast.success(data.message);
      setP({ name: "", email: "", phone: "", request: "", keep_private: false });
    } catch { toast.error("Something went wrong. Please try again."); }
    setPl(false);
  };

  return (
    <div data-testid="contact-page">
      <PageHeader eyebrow="Reach Out" title={"We'd Love to\nHear From You"} sub="Have a question, a prayer need, or just want to connect? Our doors and hearts are open." image={IMAGES.fellowship} />

      <section className="py-24 sm:py-32 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-[0.9fr_1.1fr] gap-14 lg:gap-20">
          <Reveal>
            <Eyebrow>Visit or Call</Eyebrow>
            <h2 className="font-display font-bold text-3xl mb-8">{CHURCH.name}</h2>
            <div className="space-y-5 mb-8">
              <a href={CHURCH.directions} target="_blank" rel="noopener noreferrer" className="flex gap-4 items-start hover:text-[#E63946] transition-colors">
                <MapPin className="text-[#E63946] mt-1 shrink-0" size={20} />
                <span>{CHURCH.address}<br />{CHURCH.cityStateZip}</span>
              </a>
              <a href={CHURCH.phoneHref} className="flex gap-4 items-center hover:text-[#E63946] transition-colors"><Phone className="text-[#E63946] shrink-0" size={20} />{CHURCH.phone}</a>
              <a href={`mailto:${CHURCH.email}`} className="flex gap-4 items-center hover:text-[#E63946] transition-colors"><Mail className="text-[#E63946] shrink-0" size={20} />{CHURCH.email}</a>
            </div>
            <div className="flex flex-wrap gap-3 mb-8">
              <Btn href={CHURCH.phoneHref} testid="contact-call"><Phone size={16} /> Call Us</Btn>
              <Btn href={CHURCH.directions} variant="outline" testid="contact-directions"><MapPin size={16} /> Get Directions</Btn>
            </div>
            <div className="overflow-hidden rounded-sm border border-black/10 h-[280px]">
              <iframe title="Location" src={CHURCH.mapEmbed} className="w-full h-full" style={{ border: 0 }} loading="lazy" />
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <form onSubmit={submitContact} className="border border-black/10 rounded-sm p-7 sm:p-10" data-testid="contact-form">
              <h3 className="font-display font-bold text-2xl mb-8">Send a message</h3>
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field label="First Name" name="first_name" required value={c.first_name} onChange={ch(setC)} />
                <Field label="Last Name" name="last_name" required value={c.last_name} onChange={ch(setC)} />
                <Field label="Email" name="email" type="email" required value={c.email} onChange={ch(setC)} />
                <Field label="Phone" name="phone" value={c.phone} onChange={ch(setC)} />
              </div>
              <div className="mb-6"><Field label="Subject" name="subject" required options={CONTACT_SUBJECTS} value={c.subject} onChange={ch(setC)} /></div>
              <div className="mb-8"><Field label="Message" name="message" textarea required value={c.message} onChange={ch(setC)} /></div>
              <Btn type="submit" onClick={() => {}} testid="contact-submit" disabled={cl} className="w-full">{cl ? <><Loader2 className="animate-spin" size={16} /> Sending…</> : "Send Message"}</Btn>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Prayer Request */}
      <section id="prayer" className="py-24 sm:py-32 bg-[#0A0A0A] text-white grain relative" data-testid="prayer-section">
        <div className="relative max-w-[900px] mx-auto px-5 sm:px-8">
          <Reveal className="text-center mb-12">
            <HeartHandshake className="mx-auto text-[#E63946] mb-6" size={40} />
            <h2 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl mb-5">How Can We Pray for You?</h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">We believe in the power of prayer, and we would be honored to pray with you. Share your request — nothing is too big or too small.</p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submitPrayer} className="bg-white text-[#0A0A0A] rounded-sm p-7 sm:p-10" data-testid="prayer-form">
              <div className="grid sm:grid-cols-2 gap-6 mb-6">
                <Field label="Name" name="name" required value={p.name} onChange={ch(setP)} />
                <Field label="Email" name="email" type="email" required value={p.email} onChange={ch(setP)} />
              </div>
              <div className="mb-6"><Field label="Phone (optional)" name="phone" value={p.phone} onChange={ch(setP)} /></div>
              <div className="mb-6"><Field label="Your Prayer Request" name="request" textarea required value={p.request} onChange={ch(setP)} /></div>
              <label className="flex items-center gap-3 mb-8 cursor-pointer">
                <input type="checkbox" checked={p.keep_private} onChange={(e) => setP({ ...p, keep_private: e.target.checked })} data-testid="prayer-private" className="w-5 h-5 accent-[#E63946]" />
                <span className="text-sm text-black/70">Keep this request private (shared only with our pastoral team)</span>
              </label>
              <Btn type="submit" onClick={() => {}} testid="prayer-submit" disabled={pl} className="w-full">{pl ? <><Loader2 className="animate-spin" size={16} /> Submitting…</> : "Submit Prayer Request"}</Btn>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
